import Navbar2 from "@/component/navbar2";
import BrandFooter from "@/components/BrandFooter";
import { ORIGINS } from "@/brand";
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, type ReactNode } from 'react';
import L7Video from '@/components/landing/L7Video';
import Stars from '@/components/Stars';

/* ── palette (Grow & Raise — consulting blue) ─────────────── */
const BLUE = '#1976d2';
const BLUE_D = '#1356a0';
const STEEL = '#3171af';
const NAVY = '#122f52';
const ICE = '#E8F2FD';
const ICE2 = '#F2F8FE';
const SKY = '#EAF3FE';
const INK = '#17263A';
const INK2 = '#42505F';
const MUTED = '#6E7888';
const LINE = '#E6EBF1';
const WHITE = '#fff';
const CREAM = '#FCFDFF';
const PAPER = '#F4F6F9';
const YELLOW = '#FFD54F';

const SHADOW = '0 30px 66px -30px rgba(23,38,58,.34)';
const SHADOW_S = '0 10px 26px -16px rgba(23,38,58,.22)';

const AV_YOU = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/l3.png';
const AV_ADVISOR = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/l4.png';

/* ── helpers ──────────────────────────────────────────────── */
function FadeIn({ children, delay = 0, className = '', style }: { children: ReactNode; delay?: number; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(es => { if (es[0]?.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); } }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={style}>{children}</div>;
}

function CounterStat({ to, suffix = '' }: { to: number; suffix?: string }) {
  const [val, setVal] = useState(0);
  const started = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(es => {
      if (es[0]?.isIntersecting && !started.current) {
        started.current = true;
        if (to === 0) { obs.disconnect(); return; }
        const step = Math.max(1, Math.ceil(to / 40)); let n = 0;
        const t = setInterval(() => { n += step; if (n >= to) { setVal(to); clearInterval(t); } else setVal(n); }, 22);
        obs.disconnect();
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

const Eyebrow = ({ children, color = STEEL }: { children: ReactNode; color?: string }) => (
  <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color, display: 'inline-flex', alignItems: 'center', gap: 8 }}>{children}</span>
);

const Wrap = ({ children, style }: { children: ReactNode; style?: React.CSSProperties }) => (
  <div className="max-w-[1180px] mx-auto px-6" style={style}>{children}</div>
);

const btnBase = { display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: '2px solid', cursor: 'pointer', textDecoration: 'none', transition: 'transform .16s, box-shadow .22s, background .2s, color .2s', fontFamily: 'Montserrat' } as const;
const blueBtn = { ...btnBase, background: BLUE, color: '#fff', borderColor: BLUE_D } as const;
const navyBtn = { ...btnBase, background: NAVY, color: '#fff', borderColor: NAVY } as const;
const ghostBtn = { ...btnBase, background: 'transparent', color: INK, borderColor: INK } as const;

const CAPS = [
  { t: 'Readiness score', p: 'A live, investor-grade score across financials, traction, team and deal.', icon: <><path d="M3 17l5-5 4 3 8-8" stroke={BLUE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="20" cy="7" r="2" fill={BLUE} /></> },
  { t: 'Data room', p: 'A secure, organised data room investors expect — ready for diligence.', icon: <><path d="M5 4h9l5 5v11H5z" stroke={BLUE} strokeWidth="2" /><path d="M8 12h7M8 16h5" stroke={BLUE} strokeWidth="2" strokeLinecap="round" /></> },
  { t: 'KPI dashboard', p: 'Track the metrics VCs and angels actually look for.', icon: <><path d="M4 19V5M4 19h16" stroke={STEEL} strokeWidth="2" strokeLinecap="round" /><path d="M8 15l3-4 3 2 4-6" stroke={STEEL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></> },
  { t: 'Financial model', p: 'An investor-grade three-statement model built with you.', icon: <><rect x="4" y="3" width="16" height="18" rx="2" stroke={BLUE} strokeWidth="2" /><path d="M8 8h8M8 12h8M8 16h5" stroke={BLUE} strokeWidth="2" strokeLinecap="round" /></> },
  { t: 'Pitch & valuation', p: 'Sharpen your narrative and work through a defensible valuation.', icon: <path d="M12 3l2.4 5 5.6.6-4.2 3.8 1.2 5.6L12 16.8 7 18l1.2-5.6L4 8.6 9.6 8z" stroke={STEEL} strokeWidth="2" strokeLinejoin="round" /> },
  { t: 'Investor intros', p: 'Warm paths to the right investors across MENA, UK and US.', icon: <><circle cx="6" cy="12" r="2.5" stroke={BLUE} strokeWidth="2" /><circle cx="18" cy="6" r="2.5" stroke={BLUE} strokeWidth="2" /><circle cx="18" cy="18" r="2.5" stroke={BLUE} strokeWidth="2" /><path d="M8.2 11l7.6-4M8.2 13l7.6 4" stroke={BLUE} strokeWidth="2" /></> },
];

const STEPS = [
  { h: 'Measure your readiness', p: 'Answer a short assessment and get a live investor-readiness score across the factors investors actually weigh.' },
  { h: 'Close the gaps', p: 'Advisors and fractional CFOs sharpen your model, pitch and valuation — turning weak spots into strengths.' },
  { h: 'Get in the room', p: 'Walk in prepared with an investor-grade data room, and warm intros to the right investors across MENA, UK and US.' },
];

const MARKETS = [
  { flag: 'MENA', h: 'Home market', p: "Deep roots with the region's founders and how they actually raise." },
  { flag: 'UK', h: 'Deal network', p: 'An active deal and investor network in a primary global capital hub.' },
  { flag: 'USA', h: 'On the ground', p: 'A partner presence opening the largest venture market to our founders.' },
];

const QUOTES = [
  { av: 'O', bg: BLUE, q: 'The readiness score showed exactly what to fix. Six weeks later we closed our round.', n: 'Omar H.', s: 'Fintech · Dubai' },
  { av: 'L', bg: STEEL, q: 'Our advisor rebuilt the model and pitch — investors finally took us seriously.', n: 'Lea M.', s: 'SaaS · Abu Dhabi' },
  { av: 'Y', bg: NAVY, q: 'Warm intros to the right investors across MENA. That network is everything.', n: 'Youssef A.', s: 'Marketplace · Riyadh' },
];

const FAQS = [
  { q: 'Is the readiness score really free?', a: 'Yes — you can run the readiness assessment and see your score with no signup to start. Deeper advisory work is where paid packages come in.' },
  { q: 'Do I need to be raising right now?', a: 'No. Founders use the score from idea stage through Series A to know where they stand and what to fix — long before term sheets.' },
  { q: 'What does consulting actually do?', a: 'Advisors build investor-grade financial models, refine your pitch and narrative, work through valuation, and open warm paths to the right investors.' },
  { q: 'Is this the same Conncct as the compliance side?', a: 'Yes. Conncct has two sides: Grow & Raise (here) for readiness and consulting, and Run & Comply for setup, accounting and compliance. Switch anytime.' },
];

/* ═══════════════════════════════════════════════════════════ */
export default function GrowRaise() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const SIGNUP = '/consulting/signup';

  return (
    <div style={{ background: CREAM, color: INK, fontFamily: "'Montserrat', system-ui, sans-serif", overflowX: 'clip', letterSpacing: '-0.01em' }}>
      <style>{`
        @keyframes gr-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
        @keyframes gr-toR{0%{left:-2px;opacity:0}15%{opacity:1}80%{opacity:1}100%{left:calc(100% - 6px);opacity:0}}
        @keyframes gr-toL{0%{right:-2px;opacity:0}15%{opacity:1}80%{opacity:1}100%{right:calc(100% - 6px);opacity:0}}
        .gr-btn:hover{transform:translateY(-3px)}
        .gr-ghost:hover{background:${INK};color:${CREAM} !important}
        .gr-card{transition:transform .25s cubic-bezier(.2,.7,.2,1),box-shadow .25s}
        .gr-card:hover{transform:translateY(-6px);box-shadow:${SHADOW}}
        .gr-dotL{animation:gr-toR 2.2s linear infinite}
        .gr-dotR{animation:gr-toL 2.2s linear infinite}
        .gr-dotB{animation-delay:1.1s}
        @media(max-width:1120px){.gr-flo{display:none}}
      `}</style>

      <div className="sticky top-0 z-50 w-full">
        <Navbar2 bgColor="[#E8F2FD]" logoTo="/" signupTo="/consulting/signup" />
      </div>

      {/* ── HERO ─────────────────────────────────────────── */}
      <section style={{ position: 'relative', overflow: 'hidden', background: `linear-gradient(180deg,${SKY} 0%,#eef6ff 55%,${CREAM} 100%)` }}>
        {/* floating badges */}
        <div className="gr-flo" style={{ position: 'absolute', zIndex: 3, left: 10, bottom: 44, background: 'rgba(255,255,255,.96)', border: `1px solid ${LINE}`, borderRadius: 16, boxShadow: SHADOW, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 232, animation: 'gr-float 6.5s ease-in-out infinite' }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', background: '#E1F1FD', flex: 'none' }}><svg width="18" height="18" viewBox="0 0 24 24" fill={BLUE}><path d="M12 2l2.6 6.3 6.8.5-5.2 4.4 1.7 6.6L12 16.9 6.3 20.3l1.7-6.6L2.8 9.3l6.8-.5z" /></svg></span>
          <span><b style={{ fontSize: 13, display: 'block' }}>300+ founders</b><small style={{ fontSize: 10.5, color: MUTED, fontWeight: 700 }}>Advised to raise</small></span>
        </div>
        <div className="gr-flo" style={{ position: 'absolute', zIndex: 3, right: 12, top: 98, background: 'rgba(255,255,255,.96)', border: `1px solid ${LINE}`, borderRadius: 16, boxShadow: SHADOW, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 232, animation: 'gr-float 7.5s ease-in-out infinite .6s' }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', background: '#dfe8fb', flex: 'none' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 17l5-5 4 3 8-8" stroke="#173a63" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="20" cy="7" r="2" fill="#173a63" /></svg></span>
          <span><b style={{ fontSize: 13, display: 'block' }}>Investor-grade</b><small style={{ fontSize: 10.5, color: MUTED, fontWeight: 700 }}>Data room &amp; KPIs ready</small></span>
        </div>

        <Wrap>
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-13 items-center" style={{ gap: 52, padding: '70px 0 88px', position: 'relative', zIndex: 2 }}>
            <FadeIn>
              <Eyebrow>Capital Readiness · Consulting &amp; Advisory</Eyebrow>
              <h1 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 'clamp(40px,5.6vw,68px)', letterSpacing: '-0.04em', lineHeight: 1.06, marginTop: 14 }}>
                Get investor-ready.<br />Then <span style={{ color: BLUE }}>raise.</span>
              </h1>
              <p style={{ fontSize: 20, color: INK2, maxWidth: 640, lineHeight: 1.6, margin: '24px 0 30px' }}>
                Know exactly how fundable you are with a live readiness score, fix the gaps with seasoned advisors and fractional CFOs, and walk into the room prepared — for a raise, a new market or CapEx.
              </p>
              <div style={{ display: 'flex', gap: 13, flexWrap: 'wrap' }}>
                <Link to={SIGNUP} className="gr-btn" style={blueBtn}>Get your readiness score <span aria-hidden>→</span></Link>
                <a href="#how" className="gr-btn gr-ghost" style={ghostBtn}>Talk to an advisor</a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 26, fontSize: 13.5, color: MUTED }}>
                <Stars />
                <span><b style={{ color: INK }}>Advisors who've raised &amp; exited</b> · MENA · UK · US</span>
              </div>
            </FadeIn>

            {/* connect-scene mockup */}
            <FadeIn delay={160}>
              <div style={{ position: 'relative', zIndex: 2, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 26, boxShadow: SHADOW, padding: '26px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <b style={{ fontSize: 15 }}>You + your Conncct advisor</b>
                  <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 999, background: ICE, color: BLUE_D }}>● Connected</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                  <Person img={AV_YOU} name="You" role="Founder" />
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, paddingTop: 22 }}>
                    <ConnLine dir="l" color={BLUE} />
                    <div style={{ width: 46, height: 46, borderRadius: '50%', background: INK, display: 'grid', placeItems: 'center', flex: 'none', boxShadow: '0 12px 24px -12px rgba(23,38,58,.6)', border: '3px solid #fff' }}>
                      <svg viewBox="0 0 44 16" width="24" height="10" fill="none"><line x1="11" y1="8" x2="33" y2="8" stroke="#7fc0ff" strokeWidth="2.5" /><circle cx="11" cy="8" r="5" fill="#1976D2" stroke="#7fc0ff" strokeWidth="2.5" /><circle cx="33" cy="8" r="5" fill="#fff" stroke="#7fc0ff" strokeWidth="2.5" /></svg>
                    </div>
                    <ConnLine dir="r" color={STEEL} />
                  </div>
                  <Person img={AV_ADVISOR} name="Your advisor" role="Ex-VC · CFO" />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 18 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, padding: '10px 13px', borderRadius: 14, borderBottomLeftRadius: 4, maxWidth: '47%', lineHeight: 1.35, background: ICE, color: INK }}>"How fundable are we?"</div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, padding: '10px 13px', borderRadius: 14, borderBottomRightRadius: 4, maxWidth: '47%', lineHeight: 1.35, background: '#eef4fb', color: INK, textAlign: 'right' }}>"Here's your 6-week plan."</div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                  <Metric b="72" s="Readiness" color={BLUE} />
                  <Metric b="AED 2M" s="Raise target" />
                  <Metric b="Ready" s="Data room" />
                </div>
              </div>
            </FadeIn>
          </div>
        </Wrap>
      </section>

      <L7Video />

      {/* ── SERVICES ─────────────────────────────────────── */}
      <section id="services" style={{ padding: '100px 0' }}>
        <Wrap>
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>Two services · one growth engine</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '14px 0' }}>Measure your readiness. Close the gaps. Raise.</h2>
            <p style={{ fontSize: 19, color: INK2, maxWidth: 640, margin: '0 auto', lineHeight: 1.6 }}>See where you stand, then work with advisors to get there. Pick a side to dive in.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[22px] mt-11">
            <FadeIn>
              <ServiceCard bg={`linear-gradient(160deg,${ICE},#fff)`} icon={<><path d="M3 17l5-5 4 3 8-8" stroke={BLUE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="20" cy="7" r="2" fill={BLUE} /></>}
                title="Capital Readiness"
                p="A live, investor-grade readiness score across financials, traction, team and deal — with the data room and KPI dashboard investors expect."
                feats={['Readiness score', 'Data room', 'KPI dashboard', 'Goal assessment']}
                price={<>Free readiness check · <b style={{ color: INK }}>no signup to start</b></>}
                cta={<Link to={SIGNUP} className="gr-btn" style={blueBtn}>Get your score <span aria-hidden>→</span></Link>} />
            </FadeIn>
            <FadeIn delay={100}>
              <ServiceCard bg="linear-gradient(160deg,#e7eefb,#fff)" icon={<path d="M12 3l2.4 5 5.6.6-4.2 3.8 1.2 5.6L12 16.8 7 18l1.2-5.6L4 8.6 9.6 8z" stroke={STEEL} strokeWidth="2" strokeLinejoin="round" />}
                title="Consulting & Advisory"
                p="Seasoned advisors and fractional CFOs fix what's holding the raise back — model, pitch, narrative and valuation — and open warm paths to the right investors."
                feats={['Financial model', 'Pitch & valuation', 'Fractional CFO', 'Investor intros']}
                price={<>Capital assessment from <b style={{ color: INK }}>$99</b> · advisory packages</>}
                cta={<a href="#how" className="gr-btn" style={navyBtn}>See how it works <span aria-hidden>→</span></a>} />
            </FadeIn>
          </div>
        </Wrap>
      </section>

      {/* ── WHAT'S INSIDE ────────────────────────────────── */}
      <section style={{ padding: '100px 0', background: PAPER }}>
        <Wrap>
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>What's inside</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '14px 0' }}>Everything you need to raise.</h2>
            <p style={{ fontSize: 19, color: INK2, maxWidth: 640, margin: '0 auto', lineHeight: 1.6 }}>The tools, materials and people investors expect — built with you, ready for the room.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] mt-11">
            {CAPS.map((c, i) => (
              <FadeIn key={c.t} delay={(i % 3) * 90}>
                <div className="gr-card" style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 18, padding: 24, height: '100%' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: ICE, display: 'grid', placeItems: 'center', marginBottom: 14 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">{c.icon}</svg>
                  </div>
                  <h4 style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>{c.t}</h4>
                  <p style={{ fontSize: 13.5, color: MUTED, lineHeight: 1.55 }}>{c.p}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Wrap>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────── */}
      <section id="how" style={{ padding: '100px 0', background: PAPER }}>
        <Wrap>
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>How it works</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '14px 0' }}>Score. Fix. Raise.</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] mt-11">
            {STEPS.map((s, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="gr-card" style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 18, padding: 26, height: '100%' }}>
                  <div style={{ fontSize: 34, fontWeight: 800, color: BLUE, marginBottom: 8, opacity: 0.9 }}>{`0${i + 1}`}</div>
                  <h4 style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{s.h}</h4>
                  <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.55 }}>{s.p}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Wrap>
      </section>

      {/* ── MARKETS ──────────────────────────────────────── */}
      <section style={{ padding: '100px 0' }}>
        <Wrap>
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>Three markets, day one</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '14px 0' }}>Built where the capital is.</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] mt-11">
            {MARKETS.map((m, i) => (
              <FadeIn key={m.flag} delay={i * 100}>
                <div className="gr-card" style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 18, padding: 26, height: '100%' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: BLUE }}>{m.flag}</span>
                  <h4 style={{ fontSize: 19, fontWeight: 800, margin: '8px 0 6px' }}>{m.h}</h4>
                  <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.5 }}>{m.p}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Wrap>
      </section>

      {/* ── STATS ────────────────────────────────────────── */}
      <section style={{ background: NAVY, padding: '66px 0' }}>
        <Wrap>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-[22px] text-center">
            {[{ to: 300, sfx: '+', l: 'Startups advised by our team' }, { to: 3, sfx: '', l: 'Markets — MENA, UK, US' }, { to: 80, sfx: '', l: 'Target readiness score' }, { to: 1, sfx: '', l: 'Exit behind the team' }].map((s, i) => (
              <FadeIn key={i} delay={i * 80}>
                <b style={{ fontSize: 'clamp(38px,5vw,54px)', display: 'block', color: '#7fc0ff', letterSpacing: '-0.03em', fontWeight: 800 }}><CounterStat to={s.to} suffix={s.sfx} /></b>
                <span style={{ fontSize: 13.5, color: '#b9cfe8' }}>{s.l}</span>
              </FadeIn>
            ))}
          </div>
        </Wrap>
      </section>

      {/* ── CROSS-BRAND ──────────────────────────────────── */}
      <section style={{ padding: '100px 0' }}>
        <Wrap>
          <FadeIn>
            <div style={{ background: 'linear-gradient(150deg,#111d30 0%,#17263A 55%,#101b2e 120%)', color: '#fff', borderRadius: 30, padding: '56px 44px', position: 'relative', overflow: 'hidden', border: '1px solid rgba(255,255,255,.07)' }}>
              <div aria-hidden style={{ position: 'absolute', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle,rgba(255,213,79,.38),transparent 70%)', top: -130, right: -90, filter: 'blur(16px)' }} />
              <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.7fr] gap-[30px] items-center" style={{ position: 'relative', zIndex: 2 }}>
                <div>
                  <Eyebrow color={YELLOW}>The other side of Conncct</Eyebrow>
                  <h2 style={{ color: '#fff', fontSize: 'clamp(28px,3.6vw,42px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '12px 0' }}>Great raises stand on clean books.</h2>
                  <p style={{ color: '#c3d0e0', fontSize: 16, maxWidth: 520, lineHeight: 1.6 }}>Before — or alongside — the raise, get your UAE company set up and your accounting and tax handled. The compliance side keeps your foundation solid so your numbers hold up in diligence.</p>
                  <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
                    <MiniCard b="Accounting & Tax" s="Books, VAT & Corporate Tax, filed" />
                    <MiniCard b="Corporate Services" s="Setup, licensing, visas & PRO" />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
                  <a href={ORIGINS.corporate} className="gr-btn" style={{ ...btnBase, background: YELLOW, color: INK, borderColor: '#E6B400' }}>Meet Run &amp; Comply <span aria-hidden>→</span></a>
                  <a href={`${ORIGINS.corporate}/corporate`} className="gr-btn" style={{ ...btnBase, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,.5)' }}>Set up my company</a>
                </div>
              </div>
            </div>
          </FadeIn>
        </Wrap>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────── */}
      <section style={{ padding: '100px 0' }}>
        <Wrap>
          <FadeIn className="text-center max-w-[720px] mx-auto">
            <Eyebrow>Founders &amp; advisors</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.4vw,50px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '14px 0' }}>Backed by people who've raised.</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-11">
            {QUOTES.map((r, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="gr-card" style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 20, padding: 26, boxShadow: SHADOW_S, height: '100%' }}>
                  <Stars />
                  <p style={{ fontSize: 15, margin: '12px 0 18px', lineHeight: 1.55, color: INK }}>"{r.q}"</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span style={{ width: 40, height: 40, borderRadius: '50%', background: r.bg, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, flex: 'none' }}>{r.av}</span>
                    <div><b style={{ fontSize: 14 }}>{r.n}</b><small style={{ display: 'block', color: MUTED, fontSize: 12.5 }}>{r.s}</small></div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <p className="text-center" style={{ marginTop: 14, fontSize: 11.5, color: MUTED }}>Sample testimonials — swap for your real client quotes before launch.</p>
        </Wrap>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section style={{ background: PAPER, padding: '80px 0 100px' }}>
        <Wrap>
          <FadeIn className="text-center">
            <Eyebrow>FAQ</Eyebrow>
            <h2 style={{ fontSize: 'clamp(28px,4vw,42px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 12 }}>Questions, answered.</h2>
          </FadeIn>
          <div style={{ maxWidth: 800, margin: '38px auto 0' }}>
            {FAQS.map((f, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${LINE}` }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '20px 4px', fontSize: 17, fontWeight: 700, fontFamily: 'Montserrat', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 16, color: INK }}>
                  {f.q}<span style={{ color: BLUE, fontSize: 23, transition: 'transform .3s', transform: openFaq === i ? 'rotate(45deg)' : 'none', flexShrink: 0 }}>+</span>
                </button>
                <div style={{ maxHeight: openFaq === i ? 220 : 0, overflow: 'hidden', transition: 'max-height .3s ease', color: INK2, fontSize: 15 }}>
                  <p style={{ padding: '0 4px 20px' }}>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────── */}
      <section style={{ padding: '0 0 100px' }}>
        <Wrap>
          <FadeIn>
            <div style={{ background: `linear-gradient(135deg,${NAVY},${BLUE} 130%)`, borderRadius: 30, padding: '64px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden', color: '#fff' }}>
              <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,.85) .9px,transparent .9px)', backgroundSize: '24px 24px', opacity: 0.08 }} />
              <h2 style={{ fontSize: 'clamp(32px,4.4vw,52px)', fontWeight: 800, color: '#fff', position: 'relative', letterSpacing: '-0.03em' }}>Ready your startup for investment.</h2>
              <p style={{ fontSize: 18, color: '#dbeafd', maxWidth: 540, margin: '14px auto 26px', position: 'relative' }}>Get your free readiness score, see exactly where you stand, and pick the advisory that moves you to the next stage.</p>
              <Link to={SIGNUP} className="gr-btn" style={{ ...btnBase, background: '#fff', color: BLUE, borderColor: '#fff', position: 'relative' }}>Get your readiness score <span aria-hidden>→</span></Link>
            </div>
          </FadeIn>
        </Wrap>
      </section>

      <BrandFooter />
    </div>
  );
}

/* ── sub-components ───────────────────────────────────────── */
function Person({ img, name, role }: { img: string; name: string; role: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 100, flex: 'none' }}>
      <span style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', boxShadow: SHADOW_S, border: '2px solid #fff' }}>
        <img src={img} alt={name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </span>
      <b style={{ fontSize: 13 }}>{name}</b>
      <span style={{ fontSize: 10, color: MUTED, fontWeight: 700, textAlign: 'center', lineHeight: 1.25 }}>{role}</span>
    </div>
  );
}
function ConnLine({ dir, color }: { dir: 'l' | 'r'; color: string }) {
  return (
    <div style={{ position: 'relative', flex: 1, height: 2, borderRadius: 2, background: 'linear-gradient(90deg,rgba(23,38,58,.06),rgba(23,38,58,.16))' }}>
      <i className={dir === 'l' ? 'gr-dotL' : 'gr-dotR'} style={{ position: 'absolute', top: -3, width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: '0 0 0 3px rgba(255,255,255,.7)' }} />
      <i className={`${dir === 'l' ? 'gr-dotL' : 'gr-dotR'} gr-dotB`} style={{ position: 'absolute', top: -3, width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: '0 0 0 3px rgba(255,255,255,.7)' }} />
    </div>
  );
}
function Metric({ b, s, color = INK }: { b: string; s: string; color?: string }) {
  return (
    <div style={{ flex: 1, background: '#F4F7FB', border: `1px solid ${LINE}`, borderRadius: 12, padding: '9px 8px', textAlign: 'center' }}>
      <b style={{ fontSize: 14, display: 'block', letterSpacing: '-0.01em', color }}>{b}</b>
      <span style={{ fontSize: 9.5, color: MUTED, fontWeight: 700 }}>{s}</span>
    </div>
  );
}
function MiniCard({ b, s }: { b: string; s: string }) {
  return (
    <div style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 16, padding: '16px 18px', flex: 1, minWidth: 150 }}>
      <b style={{ display: 'block', fontSize: 15, marginBottom: 3 }}>{b}</b>
      <span style={{ fontSize: 12.5, color: '#aebccd' }}>{s}</span>
    </div>
  );
}
function ServiceCard({ bg, icon, title, p, feats, price, cta }: { bg: string; icon: ReactNode; title: string; p: string; feats: string[]; price: ReactNode; cta: ReactNode }) {
  return (
    <div className="gr-card" style={{ position: 'relative', borderRadius: 26, padding: 34, overflow: 'hidden', border: `1px solid ${LINE}`, background: bg, height: '100%' }}>
      <div style={{ width: 54, height: 54, borderRadius: 15, display: 'grid', placeItems: 'center', marginBottom: 16, boxShadow: SHADOW_S, background: '#fff' }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">{icon}</svg>
      </div>
      <h3 style={{ fontSize: 25, fontWeight: 800, marginBottom: 8, letterSpacing: '-0.03em' }}>{title}</h3>
      <p style={{ color: INK2, fontSize: 14.5, lineHeight: 1.6, maxWidth: 420 }}>{p}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '16px 0 20px' }}>
        {feats.map(f => <span key={f} style={{ background: 'rgba(255,255,255,.8)', border: '1px solid rgba(0,0,0,.05)', fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999 }}>{f}</span>)}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: INK2, marginBottom: 14 }}>{price}</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cta}</div>
    </div>
  );
}
